/* PracticalAssessment — 10-question bank + demo runner integration.
 * Flow: questions → demo sessions tracking → submit → result. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PracticalAssessment() {
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [totalPts, setTotalPts] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState([]);
  const [answered, setAnswered] = useState({});
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [phase, setPhase] = useState("intro"); // intro | questions | between | demos | submitting | done
  const [demoSessions, setDemoSessions] = useState([]);
  const [demoCount, setDemoCount] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("vta_token")}` } });

  useEffect(() => { document.title = "التقييم العملي"; }, []);

  const startAssessment = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/assessment/questions/start`, {}, auth());
      setSession(data.session_id); setQuestions(data.questions); setTotalPts(data.total_points);
      setPhase("questions");
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر بدء التقييم");
    } finally { setBusy(false); }
  };

  const q = questions[qIdx];
  const isMulti = q?.id === "q04_resident_docs";

  const toggle = (oid) => {
    if (isMulti) {
      setSelected(selected.includes(oid) ? selected.filter(x => x !== oid) : [...selected, oid]);
    } else {
      setSelected([oid]);
    }
  };

  const submitAnswer = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/assessment/questions/answer`,
        { session_id: session, question_id: q.id, selected }, auth());
      setAnswered({ ...answered, [q.id]: { correct: data.correct, points: data.points_awarded, rationale: data.rationale_ar } });
      setScore(data.current_score);
      setFeedback(data.rationale_ar);
      setTimeout(() => {
        setFeedback("");
        if (qIdx + 1 < questions.length) { setQIdx(qIdx + 1); setSelected([]); }
        else { setPhase("between"); }
      }, 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الإرسال");
    } finally { setBusy(false); }
  };

  const startDemoRound = async (scenario_id) => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/demo/start`, { scenario_id }, auth());
      setDemoSessions([...demoSessions, data.session_id]);
      // Open the demo runner in same window via dedicated route with session preload
      nav(`/associate/demo?scenario=${scenario_id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر بدء الديمو");
    } finally { setBusy(false); }
  };

  const submitFinal = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/assessment/submit`,
        { session_id: session, demo_session_ids: demoSessions }, auth());
      setResult(data);
      setPhase("done");
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الإنهاء");
    } finally { setBusy(false); }
  };

  // ───── INTRO ─────
  if (phase === "intro") {
    return (
      <Layout>
        <div style={s.card}>
          <h1 style={s.h1}>التقييم العملي</h1>
          <p style={s.lead}>
            10 أسئلة عملية + سيناريو واقعي واحد على الأقل. الحد الأدنى للنجاح 70%.
          </p>
          <ul style={s.ul}>
            <li>كل سؤال بنقاط، الإجابة الخاطئة = صفر (لا توجد نقاط جزئية).</li>
            <li>الشرح يظهر بعد كل إجابة لتعلّم الفخ التشغيلي.</li>
            <li>الحالة العملية اختيارية لكنها ترفع علامتك.</li>
            <li>ممنوع الرجوع لتعديل إجابة سابقة.</li>
          </ul>
          {err && <div style={s.err}>{err}</div>}
          <button onClick={startAssessment} disabled={busy} style={s.cta} data-testid="start-assessment-btn">
            {busy ? "…" : "ابدأ التقييم"}
          </button>
        </div>
      </Layout>
    );
  }

  // ───── QUESTIONS ─────
  if (phase === "questions" && q) {
    return (
      <Layout>
        <div style={s.card}>
          <div style={s.progress}>
            <span>سؤال {qIdx + 1} / {questions.length}</span>
            <span>النقاط: <b>{score}</b> / {totalPts}</span>
          </div>
          <h2 style={s.h2}>{q.prompt_ar}</h2>
          {isMulti && <p style={{ fontSize: 11, color: "#fbbf24" }}>* اختر كل الإجابات الصحيحة (متعدد)</p>}
          <div style={{ marginTop: 12 }}>
            {q.options.map((o) => (
              <label key={o.id} style={{ ...s.optBtn, ...(selected.includes(o.id) ? s.optBtnActive : null) }}>
                <input type={isMulti ? "checkbox" : "radio"} checked={selected.includes(o.id)}
                       onChange={() => toggle(o.id)} data-testid={`opt-${q.id}-${o.id}`} style={{ marginInlineEnd: 8 }} />
                {o.label_ar}
              </label>
            ))}
          </div>
          {feedback && (
            <div style={{ ...s.fb, color: answered[q.id]?.correct ? "#10b981" : "#fca5a5" }}>
              {answered[q.id]?.correct ? "✓ صحيح — " : "✗ خطأ — "}{feedback}
            </div>
          )}
          {err && <div style={s.err}>{err}</div>}
          <button onClick={submitAnswer} disabled={busy || !selected.length || feedback}
                  style={s.cta} data-testid="submit-answer-btn">
            {busy ? "…" : "إرسال الإجابة"}
          </button>
        </div>
      </Layout>
    );
  }

  // ───── BETWEEN: offer demo round ─────
  if (phase === "between") {
    const scenarios = [
      { id: "tr_maid",       label: "تركيا — خادمة + كفيل" },
      { id: "fr_family",     label: "فرنسا — عائلة + خادمة" },
      { id: "fr_individual", label: "فرنسا — سعودي فردي" },
      { id: "ca_bil",        label: "كندا — BIL" },
      { id: "ksa_resident",  label: "شنغن — مقيم" },
    ];
    return (
      <Layout>
        <div style={s.card}>
          <h1 style={s.h1}>اختياري — جولة عملية</h1>
          <p style={s.lead}>أنهيت الأسئلة. نقاطك الحالية <b>{score}</b> من {totalPts}. شغّل سيناريو أو أكثر لرفع علامتك.</p>
          <p style={{ fontSize: 12, color: "#bbb" }}>أنجزت حتى الآن {demoCount} سيناريو من الديمو.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, marginTop: 16 }}>
            {scenarios.map((x) => (
              <button key={x.id} onClick={() => startDemoRound(x.id)}
                      style={s.demoBtn} data-testid={`demo-pick-${x.id}`}>{x.label}</button>
            ))}
          </div>
          {err && <div style={s.err}>{err}</div>}
          <div style={{ marginTop: 22, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={submitFinal} disabled={busy} style={s.cta} data-testid="submit-final-btn">
              {busy ? "…" : "إنهاء التقييم نهائياً"}
            </button>
            <button onClick={() => { setDemoCount(demoCount + 1); }} style={s.ghost} data-testid="count-demo-btn">
              سجّل اكتمال آخر سيناريو يدوياً
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // ───── DONE ─────
  if (phase === "done" && result) {
    const tierColor = result.tier === "strong_hire" ? "#10b981"
                    : result.tier === "consider"    ? "#3b82f6"
                    : result.tier === "borderline"  ? "#f59e0b" : "#ef4444";
    return (
      <Layout>
        <div style={s.card} data-testid="assessment-result">
          <h1 style={s.h1}>نتيجة التقييم النهائي</h1>
          <div style={{ ...s.tier, color: tierColor, borderColor: tierColor }}>{result.tier_ar}</div>
          <div style={s.grid}>
            <Stat label="الأسئلة" value={`${result.questions_score} / ${result.questions_max}`} />
            <Stat label="الديمو" value={`${result.demo_score} / ${result.demo_max}`} />
            <Stat label="الإجمالي" value={`${result.combined_score} / ${result.combined_max}`} />
            <Stat label="النسبة" value={`${result.percentage}%`} />
          </div>
          <button onClick={() => nav("/associate/tasks")} style={s.cta}>العودة للوحة المهام</button>
        </div>
      </Layout>
    );
  }

  return null;
}

function Stat({ label, value }) {
  return (
    <div style={s.stat}>
      <div style={{ fontSize: 11, color: "#9a9a9f" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Layout({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0f", color: "#e9e9ec", padding: "32px 14px",
                  direction: "rtl", fontFamily: "'Tajawal', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>{children}</div>
    </div>
  );
}

const s = {
  card: { background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 16, padding: 26 },
  h1: { fontSize: 24, fontWeight: 900, margin: 0, color: "#d4af37" },
  h2: { fontSize: 17, fontWeight: 700, color: "#fff", margin: "10px 0 0", lineHeight: 1.7 },
  lead: { fontSize: 13, color: "#bbb", lineHeight: 1.8, marginTop: 10 },
  ul: { color: "#cbd5e1", fontSize: 13, lineHeight: 1.9, paddingInlineStart: 22, marginTop: 12 },
  progress: { display: "flex", justifyContent: "space-between", fontSize: 12, color: "#888", marginBottom: 6 },
  optBtn: { display: "block", padding: "10px 12px", marginTop: 8, background: "#0f0f17",
            border: "1px solid #2a2a3a", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  optBtnActive: { background: "#1a1a25", border: "1px solid #d4af37" },
  fb: { marginTop: 12, padding: 10, background: "#0f0f17", borderRadius: 8, fontSize: 13 },
  cta: { marginTop: 18, padding: "11px 24px", background: "#d4af37", color: "#0a0a0f",
         border: "none", borderRadius: 10, fontWeight: 900, cursor: "pointer", fontSize: 13 },
  ghost: { marginTop: 18, padding: "10px 18px", background: "transparent", color: "#9a9a9f",
           border: "1px solid #2a2a3a", borderRadius: 10, cursor: "pointer", fontSize: 12 },
  demoBtn: { padding: "12px", background: "#1a1a25", border: "1px solid #2a2a3a", color: "#e9e9ec",
             borderRadius: 10, cursor: "pointer", fontSize: 13, textAlign: "center" },
  err: { padding: 10, background: "#3a1414", border: "1px solid #6b1f1f", borderRadius: 8,
         color: "#fca5a5", fontSize: 12, marginTop: 10 },
  tier: { padding: 16, border: "1px solid", borderRadius: 12, marginTop: 14, fontSize: 16,
          fontWeight: 800, textAlign: "center" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginTop: 14 },
  stat: { background: "#0f0f17", border: "1px solid #2a2a3a", borderRadius: 10, padding: 12, textAlign: "center" },
};
